import DatePicker, { DateObject } from "react-multi-date-picker";
import Card from "../../../../components/molecules/cards/card/Card";
import "./CrmStats.css";
import React, { useContext, useEffect, useState } from "react";
import CrmPieChart from "../../../../components/crmComponents/charts/crmPieChart/CrmPieChart";
import { formatNumberWithSpaces } from "../../../../utils/Utils";
import Checkbox from "../../../../components/molecules/formComponents/checkbox/Checkbox";
import AppContext from "../../../../context/AppContext";
import { fetchNetworkEstimationsStats, fetchNetworkUsersStats } from "../../../../api/crm/NetworkApi";
import { fetchAgencyEstimationsStats, fetchAgencyUsersStats } from "../../../../api/crm/AgencyApi";
import { useParams } from "react-router-dom";
import Toggle from "../../../../components/molecules/formComponents/toggle/Toggle";
import { fetchUsersStatsApi } from "../../../../api/crm/CrmClientsApi";
import { fetchEstimationsStatsApi } from "../../../../api/crm/CrmEstimationsApi";

function CrmStats({ isNetwork, isAgency }) {
  const { createNotification } = useContext(AppContext);
  const { agencyParam, networkParam } = useParams();

  const [calendarInterval, setCalendarInterval] = useState([
    new DateObject().set({ month: 1, day: 1 }),
    new DateObject(),
  ]);
  const [usersData, setUsersData] = useState();
  const [estimationsData, setEstimationsData] = useState({
    validated_only: false,
    unarchived_only: false,
    murs: 4894,
    fdc: 4567,
    tds: 981,
    count: 9442,
  });
  const [isUsingInterval, setIsUsingInterval] = useState(false);

  const chartsColors = {
    users: {
      "Liberty": "--sky-blue-alt",
      "Maestro": "--blue",
      "Optimum": "--gold",
      "Maestro Nestenn": "--pale-blue",
      "Optimum Nestenn": "--dark-blue-alt",
      "Liberty_old": "--sky-blue",
      "Maestro_old": "--dark-blue",
      "Optimum_old": "--light-gold",
      "Offert": "--grey",
      "Ancien abonné": "--red",
      "Non abonné": "--orange",
    },
    estimations: {
      "Fonds de commerce": "--sky-blue-alt",
      "Murs commerciaux": "--light-gold",
      "Titres de société": "--grey-alt",
    },
  };

  const calendarLocale = {
    name: "Calendrier",
    months: [
      ["janvier", "ja"],
      ["février", "fé"],
      ["mars", "ma"],
      ["avril", "av"],
      ["mai", "mi"],
      ["juin", "ju"],
      ["juillet", "ji"],
      ["août", "ao"],
      ["septembre", "se"],
      ["octobre", "oc"],
      ["novembre", "no"],
      ["décembre", "dé"],
    ],
    weekDays: [
      ["samedi", "sa"],
      ["dimanche", "di"],
      ["lundi", "lu"],
      ["mardi", "ma"],
      ["mercredi", "me"],
      ["jeudi", "je"],
      ["vendredi", "ve"],
    ],
    digits: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"],
    meridiems: [
      ["matin", "ma"],
      ["après-midi", "am"],
    ],
  };

  useEffect(() => {
    fetchUsersData();
  }, []);

  useEffect(() => {
    fetchEstimationsData(
      calendarInterval,
      isUsingInterval,
      estimationsData.validated_only,
      estimationsData.unarchived_only,
    );
  }, [calendarInterval, isUsingInterval]);

  async function fetchEstimationsData(calendarInterval, isUsingInterval, validatedOnly, unarchivedOnly) {
    if (calendarInterval.length !== 2 && isUsingInterval) return;

    let res;
    let result = {
      count: 0,
      validated_only: false,
      unarchived_only: false,
      data: {},
      percentages: {},
    };

    try {
      const params = [
        validatedOnly,
        unarchivedOnly,
        isUsingInterval && calendarInterval[0].format("YYYY-MM-DD"),
        isUsingInterval && calendarInterval[1].format("YYYY-MM-DD"),
      ];
      if (isNetwork) res = (await fetchNetworkEstimationsStats(networkParam, ...params)).data;
      else if (isAgency) res = (await fetchAgencyEstimationsStats(agencyParam, ...params)).data;
      else {
        res = (await fetchEstimationsStatsApi(...params)).data;
      }

      result.count = res.data_count;
      result.unarchived_only = res.unarchived_only;
      result.validated_only = res.validated_only;

      res.data.forEach(type => {
        result.data[type.label] = type.quantity;
        result.percentages[type.label] = getPercentage(type.quantity, res.data_count);
      });
    } catch (error) {
      createNotification(
        <>Une erreur est survenue lors de la récupération des données d'estimations. Veuillez réessayer.</>,
        "var(--red)",
        "var(--dark-blue)",
      );
    } finally {
      setEstimationsData(result);
    }
  }

  async function fetchUsersData() {
    let result = {
      count: 0,
      data: {},
      percentages: {},
    };
    let res;

    try {
      if (isNetwork) res = (await fetchNetworkUsersStats(networkParam)).data;
      else if (isAgency) res = (await fetchAgencyUsersStats(agencyParam)).data;
      else {
        res = (await fetchUsersStatsApi()).data;
      }

      result.count = res.data_count;
      res.data.forEach(type => {
        result.data[type.label] = type.quantity;
        result.percentages[type.label] = getPercentage(type.quantity, res.data_count);
      });
    } catch (error) {
      createNotification(
        <>Une erreur est survenue lors de la récupération des données utilisateurs. Veuillez réessayer.</>,
        "var(--red)",
        "var(--dark-blue)",
      );
    } finally {
      setUsersData(result);
    }
  }

  function getPercentage(quantity, total) {
    return Math.round((quantity / total) * 10000) / 100 + "%";
  }

  function toggleEstimationsValidatedOnly(estimationsData) {
    fetchEstimationsData(
      calendarInterval,
      isUsingInterval,
      !estimationsData.validated_only,
      estimationsData.unarchived_only,
    );
  }

  function toggleEstimationsUnarchivedOnly(estimationsData) {
    fetchEstimationsData(
      calendarInterval,
      isUsingInterval,
      estimationsData.validated_only,
      !estimationsData.unarchived_only,
    );
  }

  function getChartLabels(data) {
    if (!data?.data) return [];

    return Object.keys(data.data);
  }

  function getChartData(data) {
    if (!data?.data) return [];

    return Object.values(data.data);
  }

  function getChartPercentages(data) {
    if (!data?.data) return [];

    return Object.values(data.percentages);
  }

  function getChartColors(data, type) {
    if (!data?.data || !type) return [];

    return Object.keys(data.data).map(key => chartsColors[type][key]);
  }

  return (
    <div className='page-container container'>
      <Card className='crm-stats-calendar-container' padding='20px'>
        <h2 className='crm-stats-h2'>Afficher toutes les données d'estimations</h2>
        <Toggle
          checked={isUsingInterval}
          onClick={() => setIsUsingInterval(!isUsingInterval)}
          className='stats-toggle'
        />
        <h2 className='crm-stats-h2'>ou sélectionnez un intervalle de date&nbsp;:</h2>
        <DatePicker
          format='DD/MM/YYYY'
          className='crm-stats-calendar'
          locale={calendarLocale}
          value={calendarInterval}
          onChange={setCalendarInterval}
          range
        />
      </Card>
      <div className='row-1000 gap-lg mt-lg'>
        <Card padding='20px' className='crm-stats-card'>
          <h2 className='crm-stats-h2'>Utilisateurs&nbsp;:&nbsp;{formatNumberWithSpaces(usersData?.count)}</h2>
          <hr />
          <CrmPieChart
            labels={getChartLabels(usersData)}
            datasets={[
              {
                data: getChartData(usersData),
                backgroundColor: getChartColors(usersData, "users"),
              },
            ]}
            percentages={getChartPercentages(usersData)}
          />
        </Card>
        <Card padding='20px' className='crm-stats-card'>
          <h2 className='crm-stats-h2'>Estimations&nbsp;:&nbsp;{formatNumberWithSpaces(estimationsData.count)}</h2>
          <hr />
          <CrmPieChart
            labels={getChartLabels(estimationsData)}
            datasets={[
              {
                data: getChartData(estimationsData),
                backgroundColor: getChartColors(estimationsData, "estimations"),
              },
            ]}
            percentages={getChartPercentages(estimationsData)}
          />
          <div className='row'>
            <Checkbox
              className='crm-stats-checkbox'
              checked={estimationsData.validated_only}
              onClick={() => toggleEstimationsValidatedOnly(estimationsData)}
              label='Estimations terminées uniquement'
            />
            <Checkbox
              className='crm-stats-checkbox'
              checked={estimationsData.unarchived_only}
              onClick={() => toggleEstimationsUnarchivedOnly(estimationsData)}
              label='Estimations non archivées uniquement'
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

export default CrmStats;
